import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import _ from 'lodash';
import memoize from 'memoize-one';
import {v4 as uuidV4} from 'uuid';
import {enumerate} from 'ts-transformer-enumerate';

import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';

import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import Item from '~/shared/src/ci/Item';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import dataUtils from '~/shared/src/utilities/dataUtils';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    rawModeSchema,
    showAdditionalRawNamesSchema
} from '~/shared/src/validationSchemes/modules/overview';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult,
    SaveDataResult
} from '~/shared/src/utilities/modules/data';
import {GetTreeViewItemIdByDisplayAttributeResult} from '~/shared/src/utilities/modules/treeDesigner';
import {extractDataObjectsFromDataStructure} from '~/shared/src/utilities/dataStructure';

import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Headerbar from '~/frontend/src/components/base/TreeDesigner/Headerbar';
import DesignerTree, {
    ReferenceType as DesignerTreeReferenceType
} from '~/frontend/src/components/base/TreeDesigner/DesignerTree';
import Toolbar from '~/frontend/src/components/base/TreeDesigner/Toolbar';
import ResizeableLeftMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableLeftMenuPanel';
import ResizeableRightMenuPanel from '~/frontend/src/components/base/shared/panel/ResizeableRightMenuPanel';
import Form from '~/frontend/src/components/base/Form';
import TreeDesigner, {
    Mode as TreeDesignerMode,
    ReferenceType as TreeDesignerReferenceType
} from '~/frontend/src/components/base/TreeDesigner';
import AttributesForm from '~/frontend/src/components/base/ItemFinder/detail/PropertiesPanel';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import FieldWrapper from '~/frontend/src/components/base/shared/field/FieldWrapper';
import SchemaDropdown from '~/frontend/src/components/base/shared/field/SchemaDropdown';
import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';

import {
    isDataModified,
    isDataModifiedRecursive,
    MergeMode
} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {
    getPanelElusiveData,
    getPanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {FormPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {ClearConfigParameters} from '~/shared/src/utilities/modules/form';

import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    ElusiveDataState,
    PanelElusiveDataState
} from '~/frontend/src/reducers/elusiveData';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import loadData from '~/frontend/src/actions/data/loadData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import * as TreeActions from '~/frontend/src/actions/panel/treeView';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    setMessage,
    unsetMessage
} from '~/frontend/src/actions/intercom';
import {
    clearElusiveData,
    clearElusiveDataForSubpanels,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import * as CiActions from '~/frontend/src/actions/data';
import setParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import setPanelDirty, {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import setPanelInfo, {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import clearPanelProps, {ClearPanelProps} from '~/frontend/src/actions/panelManager/clearPanelProps';
import saveData, {SaveData} from '~/frontend/src/actions/panel/viewDesigner/saveData';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem, {AddItem} from '~/frontend/src/actions/data/addItem';
import addReference, {AddReference} from '~/frontend/src/actions/data/addReference';
import {DeleteReference} from '~/frontend/src/actions/data/deleteReference';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & {
    showCouldNotSavePopup? : boolean
};

type Variables = {
    [ variable : string ] : any;
};

const defaultProps = {
    /**
     * The database schema to filter data and metadata for.
     */
    dataContext: 'treeDesigner' as string,
    //
    title: 'TreeDesignerView' as string,
    //
    schema: 'DATA' as Schema,

    /**
     * The width of the menu on the left side in percent.
     */
    menuWidth: 15 as number,

    /**
     * The width of the menu on the right side in percent.
     */
    menu2Width: 20 as number,

    /**
     * The status to filter data for.
     */
    status: 'active' as Status,

    /**
     * The timestamp to filter data at.
     */
    timestamp: 'now' as string,

    /**
     * The timezone to filter data at.
     */
    timezone: 'Europe/Berlin' as string,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the tree: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    mode: 'design' as TreeDesignerMode
};

type DefaultProps = Readonly<typeof defaultProps>;

/**
 * Definition of components props
 * @memberof TreeTest
 * @property {String} dataContext='cypher' The ci-data context of this component
 * @property {Object} items CI-Data as stored in redux store
 * @property {String} itemsVersion Version of CI-Data as stored in redux store
 */
type Props = {
    uid : string;

    /**
     * The ItemId to filter data and metadata for.
     */
    itemId? : number;

    // the ciId of the existing tree
    rootItemId : number

    // the key of the new or existing tree
    rootItemKey : string;

    // the name of the new or existing tree
    rootItemName : string;

    // the id of the root item to display data in the form
    dataRootItemId? : number;

    payloadItemType? : string;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode? : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the tree: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames? : boolean;

    highlightedLayoutElementId? : number;

    selectedLayoutElementId? : number;

    referenceToCreateForNew? : ReferenceAttributesForCreation;

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    onSave? : ( rootItemId : number, dataContext : string ) => void;

    /**
     * The full elusiveData
     */
    allElusiveData? : ElusiveDataState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     * A storage for temporary needed data (not persisted to local storage via redux store persist), like
     *  - lastParams: The stringified parameters of the last loaded search. Used to determine if a new result have to be loaded from the backend.
     *  - gridData: The ordered itemIds to display in the grid.
     *  - totalRecords: The total result data count to be displayed in the DataTable.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;
};

const Actions = Object.assign( { loadData, setMessage, unsetMessage, setElusiveData, clearPanelProps, clearElusiveData, clearElusiveDataForSubpanels, setPanelInfo, setPanelDirty, addItem, addReference, closePanel }, PanelActions, TreeActions, IntercomActions, CiActions );

type SetParam = <K extends keyof ComponentProps>( panelKey : string, paramKey : K, paramValue : ComponentProps[K] ) => SetParamAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    loadData : typeof loadData;
    setParam : SetParam;
    setParams : SetParams;
    unsetParam : UnsetParam;
    clearPanelProps : ClearPanelProps;
    setMessage : typeof setMessage;
    unsetMessage : typeof unsetMessage;
    setPanelInfo : SetPanelInfo;
    setElusiveData : typeof setElusiveData;
    clearElusiveData : typeof clearElusiveData;
    clearElusiveDataForSubpanels : typeof clearElusiveDataForSubpanels;
    setPanelDirty : SetPanelDirty;
    deleteReference : DeleteReference;
    addItem : AddItem;
    addReference : AddReference;
};

type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
} & {
    closePanel : ClosePanel;
};

type AllProps = Props & DefaultProps & ActionProps;
export type ComponentProps = Partial<DefaultProps> & Props;

type State = {
    // the ciId of the currently selected tree node
    rootItemId : number;
    selectedItemParentId? : number;

    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | React.ReactNode | ( string | React.ReactNode )[];
    errorMessage? : string | React.ReactNode | ( string | React.ReactNode )[];

    /**
     * Counts up on menu scroll. Used to fire update for arrows
     */
    scrollIndicator : number;

    initialized : boolean;

    treeViewItemTypeId : number;

    showCreateNewTreeViewDialog : boolean;

    createNewTreeViewDialogKey? : string;
    createNewTreeViewDialogKeyError? : string;
    createNewTreeViewDialogSchema : Schema;
    createNewTreeViewDialogItemType? : string;

    mobile : boolean;
};


const schemata  = enumerate<YnSchema>(),
      getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


/**
 * Playground to test Tree implementation
 */
class TreeDesignerView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'TreeDesignerView';
    static panelTabTitle = 'YN.base.frontend.treeDesigner.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Tree';

    static panelPropsSchemaMsgIdPrefix = 'YN.base.frontend.treeDesigner';
    static panelPropsSchema = Joi.object()
        // panelSchema defaults
        .concat( requestBaseParametersSchema )
        .concat( Joi.object( {
            panelType      : Joi.string().valid( TreeDesignerView.panelType ),
            rootItemType   : Joi.string(),
            panelTabTitle  : Joi.string().default( TreeDesignerView.panelTabTitle ),
            panelTabIcon   : Joi.string().default( TreeDesignerView.panelTabIcon ),
            rootItemId     : Joi.number().description( 'Id of the tree.' ),
            rootItemKey    : Joi.string().description( 'Key of tree' ),
            payloadItemType: Joi.string().description( 'Item Type of the payload root item (only needed for new trees)' ),
            menuWidth      : Joi.number()
                .default( 15 )
                .description( 'The width of the left menu panel in percent of the available width.' ),
            menu2Width     : Joi.number()
                .default( 30 )
                .description( 'The width of the right menu panel in percent of the available width.' ),
            // this is only for compatibility with ItemFinder overview (if we open this panel from there)
            rawMode                : rawModeSchema,
            showAdditionalRawNames : showAdditionalRawNamesSchema,
            title                  : Joi.string(),
            referenceToCreateForNew: Joi.object( {
                srcItemId     : Joi.number()
                    .required(),
                destItemId    : Joi.number()
                    .required(),
                referenceSetId: Joi.number()
                    .required(),
                statusId      : Joi.number()
                    .required()
            } ),
            onSave                 : Joi.function()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( props.dataContext ) {
                delete global.data[ props.dataContext ];
                delete global.data[ `${props.dataContext}-meta` ];
                delete global.data[ `${props.dataContext}_data` ];
                delete global.data[ `${props.dataContext}_DataObjects` ];
            }

            // clear backend data of all loaded forms
            for ( const subPanelProps of Object.values( props.subPanelProps ?? {} ) as any[] ) {
                if ( subPanelProps.moduleTransformer === 'form' && subPanelProps.configHash ) {
                    const params : ClearConfigParameters = {
                        configHash: subPanelProps.configHash
                    };

                    await xhr.post( `${BASE_URL}/api/form/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
                }
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await TreeDesignerView.savePanelData(
                dispatch,
                getState,
                props
            );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await TreeDesignerView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'rootItemKey'> ) : string | undefined {
        const {
                  dataContext,
                  rootItemId,
                  rootItemKey
              }      = props,
              itemId = rootItemId
                  ?? dataUtils.findItem( dataContext, 'Tree', rootItemKey )?.id
                  ?? 0,
              item   = global.data[ dataContext ]?.items?.[ itemId ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${TreeDesignerView.panelType}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = rootItem.displayAttributeValue
        if ( params?.[ 1 ] ) {
            const displayAttributeValue = decodeURIComponent( params[ 1 ] );

            // resolve viewKey and rootItemId
            const res    = await xhr.post(
                      `${BASE_URL}/api/treeDesigner/getTreeViewItemIdByDisplayAttribute`,
                      {
                          displayAttributeValue
                      }
                  ),
                  result = res?.data as GetTreeViewItemIdByDisplayAttributeResult;

            if ( result ) {
                if ( result.data?.rootItemId ) {
                    retVal.rootItemId = result.data.rootItemId;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.designer.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    static async doSaveData( uid : string, dispatch : AppDispatch, successMsgParams : MsgParams | undefined = undefined ) : ReturnType<ReturnType<SaveData>> {
        const props                                              = getPanelProps( uid )?.panelProps as ComponentProps,
              elusiveData                                        = getPanelElusiveData( uid ),
              propsWithDefaults : Required<DefaultProps> & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  rootItemId,
                  onSave,
                  selectedLayoutElementId,
                  subPanelProps
              }                                                  = propsWithDefaults,
              rootItemIdForSave                                  = rootItemId ?? elusiveData?.data?.rootItemId,
              result                                             = await dispatch( saveData( uid, 'CFG', rootItemIdForSave, undefined, undefined, undefined, successMsgParams ) );

        // remap selectedLayoutElement id
        if ( selectedLayoutElementId && result && result.data?.itemMetaDataMapping?.[ String( selectedLayoutElementId ) as unknown as number ] ) {
            dispatch( setParam( uid, 'selectedLayoutElementId', result.data.itemMetaDataMapping[ String( selectedLayoutElementId ) as unknown as number ].id ) );
        }

        // remap activeTabContainerIndexes
        if ( result && result.data?.referenceMetaDataMapping && Object.keys( result.data.referenceMetaDataMapping ).length ) {
            for ( const subPanelKey of Object.keys( subPanelProps ) as string[] ) {
                if ( subPanelProps[ subPanelKey ].activeTabContainerIndexes ) {
                    const activeTabContainerIndexes    = subPanelProps[ subPanelKey ].activeTabContainerIndexes,
                          newActiveTabContainerIndexes = { ...activeTabContainerIndexes };

                    let remapped : boolean = false;

                    for ( const realTabContainerLayoutElementId of Object.keys( newActiveTabContainerIndexes ) ) {
                        if ( newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] < 0 && result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ] ) {
                            remapped = true;
                            newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] = result.data.referenceMetaDataMapping[ newActiveTabContainerIndexes[ realTabContainerLayoutElementId ] ].id;
                        }
                    }

                    if ( remapped ) {
                        dispatch( setParam(
                            `${uid}_${subPanelKey}`,
                            'activeTabContainerIndexes',
                            newActiveTabContainerIndexes
                        ) );
                    }
                }
            }
        }

        if ( onSave ) {
            // remap rootItem id
            if ( result && result.data?.itemMetaDataMapping?.[ String( rootItemIdForSave ) as unknown as number ] ) {
                onSave( result.data.itemMetaDataMapping[ String( rootItemIdForSave ) as unknown as number ].id, uid );
            }
            else {
                onSave( rootItemIdForSave, uid );
            }
        }

        return result;
    }

    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              allElusiveData                           = getState()?.elusiveData;

        let retVal : Promise<SaveDataResult | false> = Promise.resolve( false ),
            hasError : boolean                       = false;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData;

                    if ( panelElusiveData.errorData?.count ) {
                        hasError = true;
                        break;
                    }
                }
            }
        }

        // panel has errors -> show popup
        if ( hasError ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );
        }

        // panel has no errors -> save
        else {
            retVal = TreeDesignerView.doSaveData( uid, dispatch, successMsgParams );
        }

        return retVal;
    }

    mediaQuery = matchMedia( 'screen and (max-width: 960px)' );
    menuContentPanelContentStyle = { padding: 0 };
    menuContentPanelsPt = {
        gutter: {
            style: {
                display: 'none'
            }
        }
    };

    isLoading : boolean = false;

    cm : InstanceType<typeof ContextMenu> | null = null;
    toast : Toast | null = null;
    tree : DesignerTreeReferenceType | null = null;
    treeDesigner : TreeDesignerReferenceType | null = null;

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * React Component constructor
     * @param {Object} props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            rootItemId: 0 as number,

            isDirty   : false,
            errorCount: 0,

            scrollIndicator: 0 as number,

            initialized: false,

            treeViewItemTypeId: metaModel[ 'CFG' ].getItemType( 'TreeView' )?.id ?? 0,

            showCreateNewTreeViewDialog: false,

            createNewTreeViewDialogKey     : props.rootItemKey ? props.rootItemKey : undefined,
            createNewTreeViewDialogSchema  : 'DATA',
            createNewTreeViewDialogItemType: props.payloadItemType ? props.payloadItemType : undefined,

            mobile: getMobile()
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getDesignerTree = memoize( this.getDesignerTree, this.getDesignerTreeEqual );
        this.getForm = memoize( this.getForm, this.getFormEqual );
        this.getTreeDesigner = memoize( this.getTreeDesigner, this.getTreeDesignerEqual );
        this.getHeaderbar = memoize( this.getHeaderbar );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.getToolbar = memoize( this.getToolbar );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.getSelectableSchemata = memoize( this.getSelectableSchemata );
        this.getPanelConfigs = memoize( this.getPanelConfigs );
        this.getAllBelongsToReferenceSetIds = memoize( this.getAllBelongsToReferenceSetIds );

        this.onMenuScroll = _.throttle( this.onMenuScroll, 100, { trailing: true } );

        this.onMatchMobileChangeHandler = this.onMatchMobileChangeHandler.bind( this );
        this.onMenu2WidthChange = this.onMenu2WidthChange.bind( this );
        this.onMenuWidthChange = this.onMenuWidthChange.bind( this );

        this.checkForCreateNewTreePrerequisits = this.checkForCreateNewTreePrerequisits.bind( this );
        this.onCreateNewTreeViewDialogOkButtonClick = this.onCreateNewTreeViewDialogOkButtonClick.bind( this );
        this.onCreateNewTreeViewDialogHide = this.onCreateNewTreeViewDialogHide.bind( this );
        this.onCreateNewTreeViewDialogSetKey = this.onCreateNewTreeViewDialogSetKey.bind( this );
        this.onCreateNewTreeViewDialogSetSchema = this.onCreateNewTreeViewDialogSetSchema.bind( this );
        this.onCreateNewTreeViewDialogSetItemType = this.onCreateNewTreeViewDialogSetItemType.bind( this );

        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
        this.onHighlightLayoutElement = this.onHighlightLayoutElement.bind( this );
        this.onSelectLayoutElement = this.onSelectLayoutElement.bind( this );
        this.onAddTreeNode = this.onAddTreeNode.bind( this );
        this.onTreeNodeDragStart = this.onTreeNodeDragStart.bind( this );
        this.onDeleteTreeNode = this.onDeleteTreeNode.bind( this );
        this.onDataRootItemIdChange = this.onDataRootItemIdChange.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  uid,
                  rootItemId,
                  rootItemKey,
                  dataContext,
                  subPanelProps,
                  allElusiveData
              }                                              = props,
              tooltipErrors : ( string | React.ReactNode )[] = [],
              messageErrors : ( string | React.ReactNode )[] = [];

        let isDirty : boolean = rootItemId ? isDataModifiedRecursive( dataContext, 'CFG', rootItemId ) : isDataModified( dataContext, 'CFG' ),
            errorCount        = 0;

        if ( allElusiveData ) {
            for ( const elusiveDataPanelKey of Object.keys( allElusiveData ) as string[] ) {
                if ( elusiveDataPanelKey.indexOf( `${uid}_` ) === 0 ) {
                    const panelElusiveData = allElusiveData[ elusiveDataPanelKey ] as PanelElusiveDataState & PanelErrorData & PanelDirtyData,
                          panelKey         = elusiveDataPanelKey.replace( `${uid}_`, '' );

                    // only check direct sub forms
                    if ( panelKey.match( /^form-/ ) && panelKey.indexOf( '_' ) === -1 ) {
                        const panelProps   = subPanelProps?.[ panelKey ],
                              formRootItem = panelProps
                                  ? global.data[ dataContext ]?.items[ panelProps.rootItemId ]
                                  : undefined,
                              elementName  = formRootItem
                                  ? formRootItem.name
                                      ? formRootItem.name
                                      : formRootItem?.key
                                  : '',
                              elementType  = formRootItem ? formRootItem.itemType.label : 'Element';

                        isDirty = isDirty || !!panelElusiveData.isDirty;

                        if ( panelElusiveData.errorData?.count ) {
                            errorCount += panelElusiveData.errorData.count;

                            if ( panelElusiveData.errorData.tooltip?.length ) {
                                tooltipErrors.push(
                                    <>
                                        <div className="yn-tooltip-header2">
                                            {__( 'YN.base.frontend.formDesigner.error.tooltip.title', { type: elementType, name: elementName } )}
                                        </div>
                                        {panelElusiveData.errorData.tooltip}
                                    </>
                                );

                                if ( panelElusiveData.errorData.message?.length ) {
                                    messageErrors.push(
                                        <>
                                            <div className="yn-error-subtitle">
                                                {__( 'YN.base.frontend.formDesigner.error.message.title', { type: elementType, name: elementName } )}
                                            </div>
                                            <hr className="yn-error-ruler"/>
                                            <div className="yn-error-suberror">
                                                {panelElusiveData.errorData.message}
                                            </div>
                                        </>
                                    );
                                }
                            }
                        }
                    }
                }
            }
        }

        return {
            rootItemId  : rootItemId || ( dataContext && rootItemKey ? dataUtils.findItem( dataContext, 'Tree', rootItemKey )?.id ?? 0 : state.rootItemId ),
            isDirty,
            errorCount,
            errorTooltip: errorCount
                ? <div key="error" className="yn-tooltip-error">
                    <div className="yn-tooltip-header">
                        {__( 'YN.base.common.tooltip.error.title' )}
                    </div>
                    {tooltipErrors}
                </div>
                : undefined,
            errorMessage: errorCount
                ? <>
                    <div className="yn-error-message">
                        {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                    </div>
                    <hr className="yn-error-ruler"/>
                    <div className="yn-error-suberror">
                        {messageErrors}
                    </div>
                </>
                : undefined
        };
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        const {
                  dataContext,
                  subPanelProps,
                  uid
              } = this.props;

        this.mediaQuery.addEventListener( 'change', this.onMatchMobileChangeHandler );

        this.afterRender();

        // clear backend data of all loaded forms for new items
        for ( const subPanelKey of Object.keys( subPanelProps ?? {} ) as string[] ) {
            const matches = subPanelKey.match( /form-(-[0-9]*)$/ );

            if ( matches ) {
                const id = Number( matches[ 1 ] );

                if ( !global.data[ dataContext ]?.items[ id ] ) {
                    this.props.clearElusiveData( `${uid}_${subPanelKey}` );
                    this.props.clearElusiveDataForSubpanels( `${uid}_${subPanelKey}` );
                    this.props.clearPanelProps( `${uid}_${subPanelKey}` );
                }
            }
        }

        if ( !this.props.elusiveData ) {
            if ( this.props.rootItemId || this.props.rootItemKey ) {
                void this.loadData();
            }
            else {
                this.checkForCreateNewTreePrerequisits();
            }
        }
        else {
            this.setState( {
                initialized: true
            } );
        }
    }

    /**
     * REACT lifecycle method
     */
    componentWillUnmount() {
        this.mediaQuery.removeEventListener( 'change', this.onMatchMobileChangeHandler );
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : Readonly<AllProps>, prevState : Readonly<State> ) {
        const {
                  uid,
                  user
              }                  = this.props,
              {
                  createNewTreeViewDialogKey,
                  createNewTreeViewDialogSchema,
                  isDirty,
                  showCreateNewTreeViewDialog
              }                  = this.state,
              selectableSchemata = this.getSelectableSchemata( user );

        this.afterRender();

        // mark panel dirty
        if ( isDirty !== undefined && isDirty !== prevState.isDirty ) {
            this.props.setPanelDirty( uid, isDirty );
        }

        // only one selectable schemata and not set -> set it
        if ( selectableSchemata.length === 1 && createNewTreeViewDialogSchema !== selectableSchemata[ 0 ] ) {
            this.setState( { createNewTreeViewDialogSchema: selectableSchemata[ 0 ] } );
        }

        // key changed or dialog shown
        if (
            prevState.createNewTreeViewDialogKey !== createNewTreeViewDialogKey
            || (
                !prevState.showCreateNewTreeViewDialog
                && showCreateNewTreeViewDialog
            )
        ) {
            this.setState( { createNewTreeViewDialogKeyError: __( 'YN.base.finder.item.key.check.unique' ) } );
            this.checkKeyUniqueness();
        }
    }

    /**
     * REACT lifecycle method
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        return !this.isLoading;
    }

    async loadData() : Promise<boolean> {
        this.isLoading = true;

        const {
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  uid
              }      = this.props,
              retVal = await this.props.loadData?.(
                  'treeDesigner/loadConfigData',
                  dataContext,
                  dataContext,
                  undefined,
                  'CFG',
                  {
                      params   : rootItemId ? {
                          viewId: rootItemId
                      } : {
                          viewKey: rootItemKey
                      },
                      mergeMode: 'keepExisting' as MergeMode
                  },
                  undefined,
                  responseData => {
                      if ( responseData?.data.rootItemId && rootItemId !== responseData.data.rootItemId ) {
                          this.props.setParam( uid, 'rootItemId', responseData.data.rootItemId );
                      }

                      if ( !responseData?.data.items.length ) {
                          this.checkForCreateNewTreePrerequisits();
                      }
                      else {
                          this.setState( {
                              initialized: true
                          } );
                      }
                  }
              );

        this.isLoading = false;

        return retVal;
    }

    afterRender( props : AllProps = this.props as AllProps ) {
        const {
                  uid,
                  dataContext,
                  rootItemId,
                  rootItemKey,
                  intercomMessages
              }                                             = props,
              {
                  isDirty,
                  errorCount
              }                                             = this.state,
              icon                                          = this.getPanelIcon( TreeDesignerView.panelTabIcon ),
              itemTitleConfig : ItemTitleConfig | undefined = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              title                                         = this.getPanelTitle( TreeDesignerView.panelTabTitle, undefined, itemTitleConfig ),
              path                                          = TreeDesignerView.buildPanelPathFromProps( { dataContext, rootItemId, rootItemKey } );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, undefined, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage?.( this.props.uid, 'toast', key as string );
            }
        }
    }

    checkForCreateNewTreePrerequisits() {
        const {
                  createNewTreeViewDialogItemType
              } = this.state;

        // no root DataObject ItemType -> show dialog
        if ( !createNewTreeViewDialogItemType ) {
            this.setState( {
                initialized                : true,
                showCreateNewTreeViewDialog: true
            } );
        }
        else {
            this.setState( {
                initialized: true
            } );

            this.createNewTree();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  uid
              }          = this.props,
              {
                  treeViewItemTypeId,
                  createNewTreeViewDialogKey
              }          = this.state,
              requestUid = 'checkKeyUniqueness';

        if ( treeViewItemTypeId && createNewTreeViewDialogKey ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( uid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : 'CFG',
                          status  : 'all',
                          itemType: treeViewItemTypeId,
                          key     : createNewTreeViewDialogKey
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { createNewTreeViewDialogKeyError: __( 'YN.base.frontend.treeDesigner.createNewDialog.key.error.notUnique' ) } );
                    }
                    else {
                        this.setState( { createNewTreeViewDialogKeyError: '' } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.error( error );
            } );
        }
    }

    createNewTree() {
        const {
                  uid,
                  dataContext,
                  rootItemName
              }                       = this.props,
              {
                  treeViewItemTypeId,
                  createNewTreeViewDialogKey,
                  createNewTreeViewDialogSchema,
                  createNewTreeViewDialogItemType
              }                       = this.state,
              referenceToCreateForNew = this.props.referenceToCreateForNew
                  ? { ...this.props.referenceToCreateForNew }
                  : undefined,
              activeStatusId          = metaModel[ 'CFG' ].getStatus( 'active' )!.id;


        if ( createNewTreeViewDialogKey && createNewTreeViewDialogItemType ) {
            const tree                = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: treeViewItemTypeId,
                          key       : createNewTreeViewDialogKey,
                          name      : rootItemName ?? createNewTreeViewDialogItemType,
                          statusId  : activeStatusId
                      },
                      'CFG'
                  ),
                  rootDataObject      = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                          key       : `${createNewTreeViewDialogKey}:${createNewTreeViewDialogItemType}:${uuidV4().substring( 0, 8 )}`,
                          name      : createNewTreeViewDialogItemType,
                          statusId  : activeStatusId,
                          properties: [
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectSchema' )!.id,
                                  value        : createNewTreeViewDialogSchema.toLowerCase(),
                                  statusId     : activeStatusId
                              },
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DataObject', 'ObjectType' )!.id,
                                  value        : createNewTreeViewDialogItemType,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  ),
                  dynamicNodeItemType = metaModel[ 'CFG' ].getItemType( 'DynamicNode' )!,
                  dynamicNode         = this.props.addItem(
                      dataContext,
                      {
                          itemTypeId: dynamicNodeItemType.id,
                          key       : `${createNewTreeViewDialogKey}:step:${uuidV4().substring( 0, 8 )}`,
                          name      : `${dynamicNodeItemType.label}:${createNewTreeViewDialogItemType}`,
                          statusId  : activeStatusId,
                          properties: [
                              {
                                  propertySetId: metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'DynamicNode', 'Order' )!.id,
                                  value        : 10,
                                  statusId     : activeStatusId
                              }
                          ]
                      },
                      'CFG'
                  );

            if ( tree && rootDataObject && dynamicNode ) {
                this.props.addReference(
                    dataContext,
                    {
                        srcItemId     : rootDataObject.id,
                        destItemId    : tree.id,
                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'TreeView', 'isRootOf', 'src' )![ 0 ].id,
                        statusId      : activeStatusId
                    },
                    'CFG'
                );
                this.props.addReference(
                    dataContext,
                    {
                        srcItemId     : dynamicNode.id,
                        destItemId    : tree.id,
                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DynamicNode', 'TreeView', 'belongsTo', 'src' )![ 0 ].id,
                        statusId      : activeStatusId
                    },
                    'CFG'
                );
                this.props.addReference(
                    dataContext,
                    {
                        srcItemId     : rootDataObject.id,
                        destItemId    : dynamicNode.id,
                        referenceSetId: metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'DynamicNode', 'providesDataFor', 'src' )![ 0 ].id,
                        statusId      : activeStatusId
                    },
                    'CFG'
                );

                // add additionalReference (replace rootItemId by tree.id)
                if ( referenceToCreateForNew ) {
                    if ( referenceToCreateForNew.srcItemId === 0 ) {
                        referenceToCreateForNew.srcItemId = tree.id;
                    }
                    else {
                        referenceToCreateForNew.destItemId = tree.id;
                    }

                    this.props.addReference( dataContext, referenceToCreateForNew, 'CFG' );
                    this.props.setParam( uid, 'referenceToCreateForNew', undefined );
                }

                this.props.setParam( uid, 'rootItemId', tree.id );
            }
        }
    }

    onReloadButtonClick() {
        const {
                  uid,
                  dataContext
              } = this.props;

        if ( dataContext ) {
            this.isLoading = true;

            this.props.clearElusiveData?.( uid );
            this.props.clearElusiveDataForSubpanels?.( uid );

            // clean data
            global.data[ dataContext ] = {
                items     : {},
                references: {},
                schema    : global.data[ dataContext ].schema
            };

            this.treeDesigner?.onReload();
            this.treeDesigner?.clearRootItemData();

            this.setState( {}, () => {
                const asyncFnc = async () => {
                    await this.loadData();
                };

                void asyncFnc();
            } );
        }
    }

    onMenuScroll() {
        this.setState( prevState => ( { scrollIndicator: ( prevState.scrollIndicator ?? 0 ) + 1 } ) );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenuWidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menuWidth', width );
    }

    /**
     * Handler for menu width change.
     *
     * @private
     */
    onMenu2WidthChange( width : number ) {
        this.props.setParam?.( this.props.uid, 'menu2Width', width );
    }

    onCreateNewTreeViewDialogOkButtonClick() {
        this.createNewTree();
        this.setState( { showCreateNewTreeViewDialog: false } );
    }

    onCreateNewTreeViewDialogHide() {
        this.props.closePanel( this.props.uid );
    }

    onCreateNewTreeViewDialogSetKey( value : string | undefined ) {
        let key      = '',
            keyError = '';

        if ( value !== undefined ) {
            key = value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( { createNewTreeViewDialogKey: key, createNewTreeViewDialogKeyError: keyError } );
        }
    }

    onCreateNewTreeViewDialogSetSchema( value : string ) {
        const {
                  createNewTreeViewDialogItemType,
                  createNewTreeViewDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewTreeViewDialogSchema === 'CFG'
                  ? `CFG:${createNewTreeViewDialogItemType ?? ''}`
                  : createNewTreeViewDialogItemType ?? '';

        let {
                createNewTreeViewDialogKey,
                createNewTreeViewDialogKeyError
            } = this.state;

        if ( !createNewTreeViewDialogKey || createNewTreeViewDialogKey === oldGeneratedKey ) {
            createNewTreeViewDialogKey = value === 'CFG' ? 'CFG:' : '';
            createNewTreeViewDialogKeyError = undefined;
        }

        this.setState( {
            createNewTreeViewDialogSchema  : value as Schema,
            createNewTreeViewDialogItemType: undefined,
            createNewTreeViewDialogKey,
            createNewTreeViewDialogKeyError
        } );
    }

    onCreateNewTreeViewDialogSetItemType( value : string ) {
        const {
                  createNewTreeViewDialogItemType,
                  createNewTreeViewDialogSchema
              }               = this.state,
              oldGeneratedKey = createNewTreeViewDialogSchema === 'CFG'
                  ? `CFG:${createNewTreeViewDialogItemType ?? ''}`
                  : createNewTreeViewDialogItemType ?? '';

        let {
                createNewTreeViewDialogKey,
                createNewTreeViewDialogKeyError
            } = this.state;

        if ( !createNewTreeViewDialogKey || createNewTreeViewDialogKey === oldGeneratedKey ) {
            createNewTreeViewDialogKey = createNewTreeViewDialogSchema === 'CFG' ? 'CFG:' + value : value;
            createNewTreeViewDialogKeyError = undefined;
        }

        this.setState( {
            createNewTreeViewDialogItemType: value,
            createNewTreeViewDialogKey,
            createNewTreeViewDialogKeyError
        } );
    }

    onHighlightLayoutElement( highlightedLayoutElementId? : number ) {
        this.props.setParam?.( this.props.uid, 'highlightedLayoutElementId', highlightedLayoutElementId );
    }

    onSelectLayoutElement( selectedLayoutElementId? : number ) {
        this.props.setParam?.( this.props.uid, 'selectedLayoutElementId', selectedLayoutElementId );
    }

    onDataRootItemIdChange( id : number ) {
        const {
                  dataRootItemId,
                  uid
              } = this.props;

        this.props.setParam( uid, 'dataRootItemId', id );

        if ( dataRootItemId ) {
            this.treeDesigner?.clearRootItemData();
        }
    }

    onAddTreeNode( parentNodeKey : string, type : 'TreeNode' | 'DynamicNode', dataObjectId? : number ) : Item | undefined {
        return this.treeDesigner?.addTreeNode( parentNodeKey, type, dataObjectId );
    }

    onTreeNodeDragStart( nodeKey : string, event : React.DragEvent<HTMLElement> ) : void {
        return this.treeDesigner?.onDragStart( nodeKey, event );
    }

    onDeleteTreeNode( id : number ) {
        const {
                  uid,
                  subPanelProps
              } = this.props;

        this.props.clearElusiveData( `${uid}_form-${id}` );
        this.props.clearElusiveDataForSubpanels( `${uid}_form-${id}` );

        this.props.setParam(
            uid,
            'subPanelProps',
            {
                ...subPanelProps,
                [ `form-${id}` ]: undefined
            }
        );
    }

    onMatchMobileChangeHandler() {
        this.setState( { mobile: getMobile() } );
    }

    onCouldNotSavePopupOkButtonClick() {
        const { uid } = this.props;

        this.props.setElusiveData( uid, { showCouldNotSavePopup: false } );
    }

    getSelectableSchemata( user? : User ) {
        const retVal : Schema[] = [];

        for ( const schema of schemata ) {
            const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

            for ( const itemType of itemTypes ) {
                if (
                    itemType.id > 0
                    && user?.hasGenericPermissionOnItemType( 'read', itemType.id, schema )
                ) {
                    retVal.push( schema );
                    break;
                }
            }
        }

        retVal.sort();

        return retVal;
    }

    getPanelConfigs( itemTypeId? : number ) {
        const { user } = this.props;

        let retVal : FormPanelProps[] | undefined = undefined;

        if ( user && itemTypeId ) {
            retVal = user.getPanelPropsForItemType( 'CFG', itemTypeId, undefined, 'Form' ) as FormPanelProps[];
        }

        return retVal;
    }

    getAllBelongsToReferenceSetIds( itemTypeId? : number ) {
        let retVal : number[] | undefined = undefined;

        if ( itemTypeId ) {
            retVal = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( itemTypeId, '*', 'belongsTo', 'src' )?.map( rset => rset.id );
        }

        return retVal;
    }

    getCreateNewTreeViewDialog() {
        const {
                  uid,
                  user
              }                           = this.props,
              {
                  showCreateNewTreeViewDialog,
                  createNewTreeViewDialogKey,
                  createNewTreeViewDialogKeyError,
                  createNewTreeViewDialogSchema,
                  createNewTreeViewDialogItemType
              }                           = this.state,
              okDisabled                  = Boolean( !createNewTreeViewDialogKey || !createNewTreeViewDialogItemType ),
              selectableSchemata          = this.getSelectableSchemata( user ),
              style : React.CSSProperties = {
                  width            : '33rem',
                  minWidth         : '33rem',
                  [ '--min-width' ]: '33rem',
                  minHeight        : '25vh'
              };

        return <Dialog
            key={`${uid}_createNewTreeViewDialog`}
            id={`${uid}_createNewTreeViewDialog`}

            visible={showCreateNewTreeViewDialog}
            onHide={this.onCreateNewTreeViewDialogHide}
            focusOnShow={false}
            closeOnEscape={false}
            className="yn-tree-dialog yn-tree-create-new-dialog"
            style={style}
            appendTo={( document.querySelector( '.yn-tabpanel.p-tabview-panel' ) ?? undefined ) as HTMLElement | undefined}
            header={__( 'YN.base.frontend.treeDesigner.createNewDialog.title' )}
            footer={<div>
                <Button
                    id={`${uid}_okButton`}
                    key="okButton"
                    label={__( 'YN.base.common.ok' )}
                    className="p-button-outlined mr-3"
                    disabled={okDisabled}
                    onClick={this.onCreateNewTreeViewDialogOkButtonClick}
                />
                <Button
                    id={`${uid}_cancelButton`}
                    key="cancelButton"
                    label={__( 'YN.base.common.cancel' )}
                    className="p-button-secondary p-button-outlined"
                    onClick={this.onCreateNewTreeViewDialogHide}
                />
            </div>}
        >
            <StringField
                uid={`${uid}_keyField`}
                label={__( 'YN.base.frontend.treeDesigner.createNewDialog.key.label' )}
                value={createNewTreeViewDialogKey}
                errorMessage={createNewTreeViewDialogKeyError}
                style={{ width: '30rem' }}
                labelPosition="top"
                infoTextLayout="hidden"
                markDirty={false}
                onChange={this.onCreateNewTreeViewDialogSetKey}
            />
            <FieldWrapper
                uid={`${uid}_itemTypeField`}
                label={__( 'YN.base.frontend.treeDesigner.createNewDialog.itemType.label' )}
                labelPosition="top"
                infoTextLayout="hidden"
                isDirty={false}
                autoComplete={false}
            >
                {selectableSchemata.length > 1 &&
                    <SchemaDropdown
                        uid={uid}
                        value={createNewTreeViewDialogSchema}
                        onChange={this.onCreateNewTreeViewDialogSetSchema}

                        style={{ flex: '0 0 7rem', width: '7rem', paddingRight: '.5rem' }}
                    />
                }
                <ItemTypeDropdown
                    uid={uid}

                    schema={createNewTreeViewDialogSchema}
                    value={createNewTreeViewDialogItemType}
                    rawMode={false}
                    showAdditionalRawNames={false}
                    markDirty={false}
                    onChange={this.onCreateNewTreeViewDialogSetItemType}
                    inputStyle={{ width: selectableSchemata.length > 1 ? '23rem' : '30rem' }}
                />
            </FieldWrapper>
        </Dialog>;
    }

    getHeaderbar( uid : string, treeKey : string, treeName : string ) {
        return <Headerbar
            key={`${uid}_headerbar`}

            uid={uid}

            treeKey={treeKey}
            treeName={treeName}
        />;
    }

    getToolbar( uid : string, dataContext : string, rootItem : Item, dataRootItemId : number | undefined, dataItemType : string, dataSchema : Schema, mode : TreeDesignerMode, readOnly : boolean, saveButtonErrorCount : number, saveButtonErrorTooltip : React.ReactNode | React.ReactNode[], isDirty : boolean ) {
        return <Toolbar
            key={`${uid}_ItemFinderToolbar`}

            uid={uid}

            dataContext={dataContext}
            schema="CFG"

            rootItemId={rootItem?.id}
            treeViewItem={rootItem}

            dataRootItemId={dataRootItemId}
            dataItemType={dataItemType}
            dataSchema={dataSchema}
            onDataRootItemIdChange={this.onDataRootItemIdChange}

            mode={mode}

            saveButtonDisabled={readOnly}
            saveButtonErrorCount={saveButtonErrorCount}
            saveButtonErrorTooltip={saveButtonErrorTooltip}
            deleteButtonDisabled={readOnly}

            onReloadButtonClick={this.onReloadButtonClick}

            showModifiedHint={isDirty}
        />;
    }

    getDesignerTree( uid : string, dataContext : string, treeProps : any, parentUid : string, rootItemId : number, additionalElusiveDataContexts : string[], selectedLayoutElementId? : number, highlightedLayoutElementId? : number ) {
        return <DesignerTree
            key={uid}

            ref={( el ) => this.tree = el}

            {...treeProps}

            uid={uid}
            dataContext={dataContext}
            parentUid={parentUid}
            rootItemId={rootItemId}

            additionalElusiveDataContexts={additionalElusiveDataContexts}

            highlightedLayoutElementId={highlightedLayoutElementId}
            onHighlightLayoutElement={this.onHighlightLayoutElement}

            selectedLayoutElementId={selectedLayoutElementId}
            onSelectLayoutElement={this.onSelectLayoutElement}

            onAddTreeNode={this.onAddTreeNode}
            onTreeNodeDragStart={this.onTreeNodeDragStart}

            className="yn-sidebar-content p-tree"
        />;
    }

    getDesignerTreeEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getTreeDesigner( uid : string, dataContext : string, treeDesignerProps : any, rootItemId : number, dataRootItemId : number | undefined, mode : TreeDesignerMode, readOnly : boolean, additionalElusiveDataContexts : string[], selectedLayoutElementId? : number, highlightedLayoutElementId? : number ) {
        return <TreeDesigner
            key={uid}

            ref={( el ) => this.treeDesigner = el}

            {...treeDesignerProps}

            uid={uid}

            dataContext={dataContext}
            rootItemId={rootItemId}
            dataRootItemId={dataRootItemId}

            mode={mode}
            readOnly={readOnly}
            additionalElusiveDataContexts={additionalElusiveDataContexts}

            highlightedLayoutElementId={highlightedLayoutElementId}
            onHighlightLayoutElement={this.onHighlightLayoutElement}

            selectedLayoutElementId={selectedLayoutElementId}
            onSelectLayoutElement={this.onSelectLayoutElement}

            onDeleteTreeNode={this.onDeleteTreeNode}
        />;
    }

    getTreeDesignerEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    getForm( uid : string, dataContext : string, formProps : any, additionalElusiveDataContexts : string[], viewKey : string, rootItemId : number, treeParentItemId : number, treeRootItemId : number, treeVariables : Variables | undefined ) {
        return <Form
            key={uid}

            {...formProps}

            uid={uid}
            dataContext={dataContext}
            additionalElusiveDataContexts={additionalElusiveDataContexts}
            schema="CFG"
            viewKey={viewKey}
            mode="embedded"
            rootItemId={rootItemId}
            rootItemKey=""
            rootItemType=""
            treeParentItemId={treeParentItemId}
            treeRootItemId={treeRootItemId}
            treeVariables={treeVariables}
        />;
    }

    getFormEqual( newArgs : any[], lastArgs : any[] ) : boolean {
        return objectUtils.getDiffKeys( { ...newArgs }, { ...lastArgs } ).length === 0;
    }

    /**
     * React render method
     * @return {*}
     */
    render() {
        const {
                  dataContext,
                  dataRootItemId,
                  elusiveData,
                  highlightedLayoutElementId,
                  menu2Width,
                  menuWidth,
                  mode,
                  selectedLayoutElementId,
                  status,
                  subPanelProps,
                  timestamp,
                  timezone,
                  uid,
                  user
              }                           = this.props as AllProps,
              {
                  errorCount,
                  errorMessage,
                  errorTooltip,
                  initialized,
                  isDirty,
                  rootItemId = 0,
                  showCreateNewTreeViewDialog
              }                           = this.state,
              rootItem                    = global.data[ dataContext ]?.items?.[ rootItemId ],
              dataStructure               = this.treeDesigner?.dataStructure,
              dataStructureSchema         = ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema,
              dataObjectItems             = dataStructure
                  ? extractDataObjectsFromDataStructure( dataStructure ).map( dO => global.data[ dataContext ]?.items?.[ dO.origId ] )
                  : [],
              /*metaModelIdsInDataStructure = dataStructure
                  ? resolveMetaModelIdsInDataStructure( dataStructure, dataStructureSchema )
                  : undefined,*/
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              readOnly                    = !( user?.isMemberOfGroup( config.sysGroups?.admin ?? '' ) || !rootItem?.properties.get( metaModel[ 'CFG' ].getPropertyType( 'SystemRelevant' )?.id ?? 0 )?.value ),
              rawMode                     = false,
              showAdditionalRawNames      = false,
              selectedLayoutElement       = selectedLayoutElementId
                  ? global.data[ dataContext ]?.items[ selectedLayoutElementId ]
                  : undefined,
              currentItemId               = selectedLayoutElement?.id ?? rootItemId,
              currentItem                 = selectedLayoutElement ?? rootItem,
              panelConfigs                = this.getPanelConfigs( currentItem?.itemTypeId ),
              panelConfig                 = panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^TVdSp_/ ) )
                  ?? panelConfigs?.find( panelProps => panelProps.viewKey?.match( /^XdSp_/ ) ) ?? panelConfigs?.[ 0 ] ?? null,
              allBelongsToReferenceSetIds = this.getAllBelongsToReferenceSetIds( currentItem?.itemTypeId ),
              parentPayloadId             = dataUtils.findReferences( dataContext, allBelongsToReferenceSetIds, undefined, currentItemId, true )?.[ 0 ]?.destItemId ?? rootItemId,
              designerTreeUid             = `tree-${rootItemId}`,
              fullDesignerTreeUid         = `${uid}_${designerTreeUid}`,
              formUid                     = `form-${currentItemId}`,
              fullFormUid                 = `${uid}_${formUid}`,
              treeDesignerUid             = `treeDesigner-${rootItemId}`,
              fullTreeDesignerUid         = `${uid}_${treeDesignerUid}`,
              attributesUid               = `attributes-${currentItemId}`,
              fullAttributesUid           = `${uid}_${attributesUid}`,
              //
              headerbar                   = this.getHeaderbar(
                  uid,
                  rootItem?.key ?? '',
                  rootItem?.name ?? ''
              ),
              toolbar                     = this.getToolbar(
                  uid,
                  dataContext,
                  rootItem,
                  dataStructure ? dataRootItemId : undefined,
                  dataStructure?.item.objectType ?? '',
                  dataStructureSchema,
                  mode,
                  readOnly,
                  errorCount,
                  errorTooltip,
                  isDirty
              ),
              menu                        = initialized && !showCreateNewTreeViewDialog
                  ? this.getDesignerTree(
                      fullDesignerTreeUid,
                      dataContext,
                      subPanelProps?.[ designerTreeUid ],
                      uid,
                      rootItemId,
                      [ fullTreeDesignerUid, fullFormUid, uid ],
                      selectedLayoutElementId,
                      highlightedLayoutElementId
                  )
                  : <></>,
              designer                    = initialized && !showCreateNewTreeViewDialog
                  ? this.getTreeDesigner(
                      fullTreeDesignerUid,
                      dataContext,
                      subPanelProps?.[ treeDesignerUid ],
                      rootItemId,
                      dataRootItemId,
                      mode,
                      readOnly,
                      [ fullDesignerTreeUid, fullFormUid, uid ],
                      selectedLayoutElementId,
                      highlightedLayoutElementId
                  )
                  : undefined,
              form                        = initialized && !showCreateNewTreeViewDialog && panelConfig?.panelType === 'Form' && currentItemId
                  ? this.getForm(
                      fullFormUid,
                      dataContext,
                      subPanelProps?.[ formUid ],
                      [ fullDesignerTreeUid, fullTreeDesignerUid, uid ],
                      panelConfig.viewKey,
                      currentItemId,
                      parentPayloadId,
                      rootItemId,
                      {
                          dataObjectItems
                      }
                  )
                  : undefined,
              genericForm                 = initialized && panelConfig?.panelType !== 'Form' && currentItem
                  ? <div key={`${uid}_formview`}>
                      <AttributesForm
                          key={fullAttributesUid}

                          {...subPanelProps?.[ attributesUid ]}

                          uid={fullAttributesUid}
                          dataContext={dataContext}
                          style={{ padding: 0 }}
                          schema="CFG"
                          status={status}
                          timestamp={timestamp}
                          timezone={timezone}
                          item={currentItem}
                          rawMode={rawMode}
                          showAdditionalRawNames={showAdditionalRawNames}
                          editable
                      />
                  </div>
                  : undefined;

        return <>
            {showCreateNewTreeViewDialog &&
                this.getCreateNewTreeViewDialog()
            }
            {elusiveData?.showCouldNotSavePopup &&
                <Dialog
                    key={`${uid}_dialog`}
                    header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                    footer={<div>
                        <Button
                            label="OK"
                            className="p-button-outlined"
                            onClick={this.onCouldNotSavePopupOkButtonClick}
                        />
                    </div>}
                    maskClassName="yn-dialog-panel-modal"
                    appendTo="self"
                    position="top"
                    visible={true}
                    style={{ width: 'auto', maxWidth: '80%' }}
                    closable={false}
                    className="yn-error-dialog"
                    onHide={() => {
                    }}
                >
                    {errorMessage}
                </Dialog>
            }
            {headerbar}
            {toolbar}
            <LoadingMaskPanel forComponent={uid} className="yn-flex-layout-parent yn-tree-wrapper">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: '0' }}
                />
                <ResizeableLeftMenuPanel
                    key={`${uid}_menuContentPanel`}

                    uid={uid}

                    gutterSize={mode === 'input' ? 0 : undefined}

                    menuWidth={menuWidth}
                    minMenuWidth={15}
                    maxMenuWidth={25}
                    onMenuScroll={this.onMenuScroll}
                    onMenuWidthChange={this.onMenuWidthChange}
                    menuClassName={mode === 'input' ? 'hidden' : undefined}
                    menu={menu}

                    contentStyle={this.menuContentPanelContentStyle}
                    content={<div className="yn-flex-layout-parent" style={{ width: '100%' }}>
                        <ResizeableRightMenuPanel
                            key={`${uid}_menu2ContentPanel`}

                            uid={uid}

                            gutterSize={mode === 'input' ? 0 : undefined}

                            contentClassName={( mode === 'input' ) ? '' : undefined}
                            content={<LoadingMaskPanel
                                forComponent={fullTreeDesignerUid}
                                className="yn-flex-layout-parent yn-content-panel-container p-0"
                                style={{ position: 'absolute', width: '100% !important', height: 'calc(100% + .5rem) !important' }}
                            >
                                {designer}
                            </LoadingMaskPanel>}

                            menuWidth={menu2Width}
                            minMenuWidth={15}
                            maxMenuWidth={33}
                            onMenuWidthChange={this.onMenu2WidthChange}
                            menuClassName={`yn-content-panel-container yn-menu-form yn-formdesigner-properties-panel ${mode === 'input' ? 'hidden' : ''}`}
                            menu={initialized
                                ? <LoadingMaskPanel
                                    key={`${uid}_treeview_loadingmask`}
                                    forComponent={panelConfig?.panelType === 'Form' ? fullFormUid : fullAttributesUid}
                                    className="yn-flex-layout-parent"
                                >
                                    <div>
                                        {form}
                                        {genericForm}
                                    </div>
                                </LoadingMaskPanel>
                                : <></>
                            }

                            pt={mode === 'input'
                                ? this.menuContentPanelsPt
                                : undefined}
                        />
                    </div>}

                    pt={mode === 'input'
                        ? this.menuContentPanelsPt
                        : undefined}
                />
            </LoadingMaskPanel>
        </>;
    }
}


// redux stuff

/**
 *
 * @param state
 * @param ownProps
 */
function mapStateToProps( state : StoreState, ownProps : AllProps ) {
    const retVal : Partial<Props> = {};

    retVal.allElusiveData = state.elusiveData;

    if ( ownProps.uid ) {

        // get elusiveData
        if ( state.elusiveData?.[ ownProps.uid ] ) {
            retVal.elusiveData = state.elusiveData[ ownProps.uid ];
        }

        // get intercom messages
        if ( state.intercom?.[ ownProps.uid ] ) {
            retVal.intercomMessages = state.intercom[ ownProps.uid ];
        }

        // get the running xhr request belonging to this component (uid)
        if ( state.xhr?.[ ownProps.uid ] ) {
            retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
        }
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * Bind action creators to component props
 * @param dispatch
 * @return {*}
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect( mapStateToProps, mapDispatchToProps )( TreeDesignerView );
